/* Admin · Free Sample Design settings.
 *
 * Reads/writes GET/PUT /api/admin/sample-design — pick which paid
 * design is offered as the free sample, toggle the campaign on/off,
 * customise the lead-capture copy, and surface the live redemption
 * count.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Gift,
  ToggleLeft,
  ToggleRight,
  Save,
  Loader2,
  Users,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { adminGet, adminPut } from "@/lib/adminApi";

const SampleDesignPanel = ({ onToast }) => {
  const [cfg, setCfg] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([
      adminGet("/admin/sample-design"),
      // Library list is public — we keep axios here because it doesn't
      // require credentials. Failure of the library list is non-fatal.
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/library`, {
        params: { page_size: 60 },
      }).then((r) => ({ ok: true, data: r.data })).catch((e) => ({ ok: false, error: e?.message })),
    ])
      .then(([cfgRes, libRes]) => {
        if (cancelled) return;
        if (!cfgRes.ok) {
          setLoadError({ status: cfgRes.status, message: cfgRes.error });
          return;
        }
        const c = cfgRes.data || {};
        setCfg({
          enabled: !!c.enabled,
          design_id: c.design_id || "",
          headline: c.headline || "",
          subtext: c.subtext || "",
          cta_label: c.cta_label || "",
          support_phone: c.support_phone || "9717667115",
          redemption_count: c.redemption_count || 0,
        });
        setDesigns(libRes.ok ? (libRes.data?.items || []) : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cancel = load();
    return cancel;
  }, [load]);

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    if (!cfg) return;
    if (!cfg.design_id) {
      onToast?.("Pick a design before saving", "error");
      return;
    }
    setSaving(true);
    const res = await adminPut("/admin/sample-design", {
      enabled: cfg.enabled,
      design_id: cfg.design_id,
      headline: cfg.headline,
      subtext: cfg.subtext,
      cta_label: cfg.cta_label,
      support_phone: cfg.support_phone,
    });
    setSaving(false);
    if (!res.ok) {
      onToast?.(res.error || "Save failed — please retry", "error");
      return;
    }
    setCfg((c) => ({ ...c, ...(res.data || {}) }));
    onToast?.("Sample design settings saved");
  };

  if (loadError && !cfg) {
    return (
      <section
        className="mb-8 rounded-3xl bg-amber-50 border-2 border-amber-300 p-6"
        data-testid="admin-sample-design-error"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-extrabold text-amber-900">
              Sample-design settings failed to load
            </h3>
            <p className="text-[13px] text-amber-900/90 mt-1">
              {loadError.message}{" "}
              {loadError.status ? <span className="font-mono text-[11px]">(HTTP {loadError.status})</span> : null}
            </p>
            <button
              onClick={load}
              data-testid="admin-sample-design-retry"
              className="mt-3 min-h-[40px] px-3 rounded-xl bg-navy text-gold font-extrabold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-navy-deep"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (loading || !cfg) {
    return (
      <section
        className="mb-8 rounded-2xl bg-white border border-border p-6 text-center text-muted-foreground"
        data-testid="admin-sample-design-loading"
      >
        Loading sample-design settings…
      </section>
    );
  }

  const selected = designs.find((d) => d.id === cfg.design_id);

  return (
    <section
      className="mb-8 rounded-3xl bg-white border-2 border-gold/30 shadow-[0_24px_48px_-24px_rgba(11,37,69,0.20)] overflow-hidden"
      data-testid="admin-sample-design-panel"
    >
      <header className="bg-gradient-to-r from-gold-dark via-gold to-gold-dark text-navy p-5 flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-navy text-gold grid place-items-center shrink-0 shadow-md">
          <Gift className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold">
            Lead Capture · Free Sample Design
          </div>
          <div className="text-[15px] font-extrabold tracking-tight">
            One free sample per verified mobile · Active design + copy
          </div>
        </div>
        <div
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-navy text-gold text-[11.5px] font-extrabold"
          data-testid="admin-sample-design-redemption-count"
        >
          <Users className="h-3.5 w-3.5" /> {cfg.redemption_count} claimed
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-sample-design-save"
          className="min-h-[42px] px-4 rounded-xl bg-navy text-gold font-extrabold text-[13.5px] inline-flex items-center gap-1.5 hover:bg-navy-deep disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save"}
        </button>
      </header>

      <div className="p-5 sm:p-6 space-y-5">
        {/* Campaign toggle */}
        <label className="flex items-center gap-3 p-3 rounded-xl border border-border bg-white cursor-pointer">
          <button
            type="button"
            onClick={() => setField("enabled", !cfg.enabled)}
            data-testid="admin-sample-design-enabled-toggle"
            className="shrink-0"
            aria-pressed={cfg.enabled}
          >
            {cfg.enabled ? (
              <ToggleRight className="h-7 w-7 text-emerald-600" />
            ) : (
              <ToggleLeft className="h-7 w-7 text-muted-foreground" />
            )}
          </button>
          <div className="min-w-0">
            <div className="text-[13.5px] font-bold text-navy">
              Campaign {cfg.enabled ? "ACTIVE" : "PAUSED"}
            </div>
            <div className="text-[11.5px] text-muted-foreground leading-snug">
              Master switch. When off, the landing banner and /free-sample
              page hide automatically — old claims are kept.
            </div>
          </div>
        </label>

        {/* Design picker */}
        <div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
              Sample design (chosen by Mera Naksha — customers cannot pick freely)
            </span>
            <select
              value={cfg.design_id}
              onChange={(e) => setField("design_id", e.target.value)}
              data-testid="admin-sample-design-select"
              className="mt-1 w-full min-h-[44px] rounded-xl border-2 border-border bg-white px-3 font-bold text-navy text-[14px] focus:border-gold focus:outline-none"
            >
              <option value="">— pick a design —</option>
              {designs.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} · {d.size_label} · {d.bhk} BHK · {d.num_floors || 1}fl · ₹{d.price}
                </option>
              ))}
            </select>
          </label>
          {selected && (
            <div className="mt-2 text-[11.5px] text-muted-foreground inline-flex items-start gap-1.5 leading-snug">
              <AlertTriangle className="h-3 w-3 mt-0.5 text-amber-600" />
              This design will be downloaded by every verified customer
              for free. Pick a representative drawing — not your best seller.
            </div>
          )}
        </div>

        {/* Copy */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
              Headline
            </span>
            <input
              type="text"
              value={cfg.headline}
              onChange={(e) => setField("headline", e.target.value)}
              data-testid="admin-sample-design-headline"
              className="mt-1 w-full min-h-[44px] rounded-xl border-2 border-border bg-white px-3 font-bold text-navy text-[14px]"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
              CTA button label
            </span>
            <input
              type="text"
              value={cfg.cta_label}
              onChange={(e) => setField("cta_label", e.target.value)}
              data-testid="admin-sample-design-cta-label"
              className="mt-1 w-full min-h-[44px] rounded-xl border-2 border-border bg-white px-3 font-bold text-navy text-[14px]"
            />
          </label>
          <label className="block lg:col-span-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
              Sub-text
            </span>
            <textarea
              rows={3}
              value={cfg.subtext}
              onChange={(e) => setField("subtext", e.target.value)}
              data-testid="admin-sample-design-subtext"
              className="mt-1 w-full rounded-xl border-2 border-border bg-white px-3 py-2 text-navy text-[13.5px] leading-relaxed"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
              Support phone (call + WhatsApp)
            </span>
            <input
              type="tel"
              value={cfg.support_phone}
              onChange={(e) => setField("support_phone", e.target.value)}
              data-testid="admin-sample-design-support-phone"
              className="mt-1 w-full min-h-[44px] rounded-xl border-2 border-border bg-white px-3 font-bold text-navy text-[14px] tabular-nums"
            />
          </label>
        </div>
      </div>
    </section>
  );
};

export default SampleDesignPanel;
